/**
 * Barres empilées par état (design system, GraphiquePrevisions et GraphiqueActivite) :
 * acquises sur la ligne de base, apprentissage, nouvelles au sommet. La colonne courante
 * (aujourd'hui) porte son total, seule valeur écrite dans le tracé.
 */
import { Fragment, useRef } from 'react';
import { dureeCourte } from '../format';
import { pluriel } from '../outils';
import {
  BAS,
  CadreDeGraphique,
  cheminBarre,
  graduer,
  HAUT,
  HAUTEUR,
  largeurTexte,
  nombre,
  useColonnes,
  useInfobulle,
  type LigneDuTableau,
} from './Graphique';

export interface ColonneEnBarres {
  nouvelles: number;
  apprentissage: number;
  acquises: number;
  /** Temps de réponse cumulé (activité seulement). */
  secondes?: number;
}

/** Empilement, de la ligne de base vers le haut. */
const ETATS = [
  { cle: 'acquise', champ: 'acquises', nom: 'Acquises', mot: 'acquises' },
  { cle: 'apprentissage', champ: 'apprentissage', nom: 'Apprentissage', mot: 'en apprentissage' },
  { cle: 'nouvelle', champ: 'nouvelles', nom: 'Nouvelles', mot: 'nouvelles' },
] as const;

export const totalColonne = (c: ColonneEnBarres) => c.nouvelles + c.apprentissage + c.acquises;

export interface GraphiqueEnBarresProps {
  classe: 'fc-graphique-previsions' | 'fc-graphique-activite';
  titre: string;
  resume: string;
  colonnes: ColonneEnBarres[];
  /** Index de la colonne courante (aujourd'hui) : premier pour les prévisions, dernier sinon. */
  courante: number;
  /** Nom de la colonne en début de phrase : « Aujourd’hui », « Samedi 26 septembre ». */
  nom: (i: number) => string;
  /** Titre de l'infobulle. */
  titreInfobulle: (i: number) => string;
  /** Étiquette de l'axe sous la colonne, ou `null`. */
  etiquette: (i: number) => string | null;
  /** « due » (prévisions) ou « révisée » (activité). */
  participe: 'due' | 'révisée';
  /** Nom du groupe des colonnes (« Cartes dues par jour, du … au … »). */
  nomDuGroupe: string;
  vide: { titre: string; texte: string } | null;
  chargement: boolean;
  tableau: { legende: string; entetes: string[]; lignes: LigneDuTableau[] };
}

export function GraphiqueEnBarres({
  classe: P,
  titre,
  resume,
  colonnes,
  courante,
  nom,
  titreInfobulle,
  etiquette,
  participe,
  nomDuGroupe,
  vide,
  chargement,
  tableau,
}: GraphiqueEnBarresProps) {
  const n = colonnes.length;
  const { actif, courant, groupe, trace, survoler, quitter } = useColonnes(n, courante);
  const bulle = useRef<HTMLDivElement>(null);
  const avecTemps = participe === 'révisée';

  const phrase = (c: ColonneEnBarres, i: number) => {
    const t = totalColonne(c);
    if (!t) return `${nom(i)} : ${avecTemps ? 'aucune révision' : 'aucune carte due'}`;
    const parties = [...ETATS]
      .reverse()
      .filter((e) => c[e.champ])
      .map((e) => `${c[e.champ]} ${e.mot}`);
    const detail =
      parties.length > 1
        ? `, dont ${parties.slice(0, -1).join(', ')} et ${parties.at(-1)}`
        : `, ${parties[0]}`;
    const temps = avecTemps ? ` en ${dureeCourte((c.secondes ?? 0) / 60)}` : '';
    return `${nom(i)} : ${pluriel(t, `carte ${participe}`, `cartes ${participe}s`)}${temps}${detail}`;
  };

  return (
    <CadreDeGraphique
      classe={P}
      titre={titre}
      resume={resume}
      vide={vide}
      chargement={chargement}
      tableau={tableau}
      trace={trace}
      legende={[...ETATS].reverse().map((e) => (
        <li key={e.cle} className={`${P}__cle`}>
          <span className={`${P}__pastille ${P}__pastille--${e.cle}`} />
          {e.nom}
        </li>
      ))}
    >
      {(L) => {
        if (!L || n === 0) return null;
        const HT = HAUTEUR;
        const H = HAUT + HT + BAS;
        // Une colonne courante bien plus haute que toutes les autres (cartes en retard) écraserait
        // l'échelle : elle est écrêtée au sommet du tracé, sa valeur reste écrite au-dessus.
        const totaux = colonnes.map(totalColonne);
        const maxAutres = Math.max(0, ...totaux.filter((_, i) => i !== courante));
        const ecretee = maxAutres > 0 && (totaux[courante] ?? 0) > 3 * maxAutres;
        const { pas, haut } = graduer(Math.max(ecretee ? 2 * maxAutres : Math.max(...totaux), 1));
        const y = (v: number) => HAUT + HT - (Math.min(v, haut) / haut) * HT;
        const graduations: number[] = [];
        for (let v = 0; v <= haut; v += pas) graduations.push(v);
        const G = Math.ceil(Math.max(...graduations.map((v) => largeurTexte(nombre(v))))) + 8;
        const pasX = (L - G) / n;
        const lb = Math.max(4, Math.min(16, Math.round(pasX * 0.55)));
        const xBarre = (i: number) => Math.round(G + i * pasX + (pasX - lb) / 2);
        const colonneActive = actif >= 0 ? colonnes[actif] : undefined;
        let finEtiquette = -Infinity;

        return (
          <>
            <svg
              className={`${P}__svg`}
              width={L}
              height={H}
              viewBox={`0 0 ${L} ${H}`}
              onPointerMove={(e) => survoler(e, G, pasX)}
              onPointerLeave={quitter}
            >
              <defs>
                <pattern
                  id={`${P}-hachures`}
                  width={4}
                  height={4}
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(45)"
                >
                  <rect className={`${P}__hachures-fond`} width={4} height={4} />
                  <line className={`${P}__hachures-trait`} x1={1} y1={0} x2={1} y2={4} />
                </pattern>
              </defs>
              <g aria-hidden="true">
                {graduations.map((v) => (
                  <text
                    key={`t${v}`}
                    className={`${P}__graduation`}
                    textAnchor="end"
                    dy="0.32em"
                    x={G - 8}
                    y={y(v)}
                  >
                    {nombre(v)}
                  </text>
                ))}
                {graduations.map((v) => {
                  const yy = Math.round(y(v)) + 0.5;
                  return (
                    <line key={`l${v}`} className={`${P}__grille`} x1={G} x2={L} y1={yy} y2={yy} />
                  );
                })}
              </g>
              <g role="group" aria-label={nomDuGroupe} ref={groupe}>
                {colonnes.map((c, i) => {
                  const xb = xBarre(i);
                  const t = totalColonne(c);
                  const parts = ETATS.filter((e) => c[e.champ] > 0);
                  let cumul = 0;
                  return (
                    <g
                      key={i}
                      className={`${P}__colonne`}
                      role="img"
                      tabIndex={i === courant ? 0 : -1}
                      aria-label={phrase(c, i)}
                      aria-current={i === courante ? 'date' : undefined}
                      data-index={i}
                      data-actif={i === actif ? '' : undefined}
                    >
                      <rect
                        className={`${P}__cible`}
                        x={G + i * pasX + 1}
                        y={HAUT - 8}
                        width={Math.max(0, pasX - 2)}
                        height={HT + 8}
                        rx={4}
                      />
                      {t === 0 && avecTemps && (
                        <rect
                          className={`${P}__sans-revision`}
                          x={xb}
                          y={HAUT + HT - 2}
                          width={lb}
                          height={2}
                          rx={1}
                        />
                      )}
                      {parts.map((e, k) => {
                        const yBas = Math.round(y(cumul));
                        cumul += c[e.champ];
                        const dessus = k === parts.length - 1;
                        // 2 px de surface entre deux segments
                        let yHaut = Math.round(y(cumul)) + (dessus ? 0 : 2);
                        if (yBas - yHaut < 2) yHaut = yBas - 2;
                        return (
                          <path
                            key={e.cle}
                            className={`${P}__segment ${P}__segment--${e.cle}`}
                            d={cheminBarre(xb, yBas, yHaut, lb, dessus)}
                          />
                        );
                      })}
                      {i === courante && ecretee && (
                        <rect
                          className={`${P}__coupure`}
                          x={xb - 2}
                          y={Math.round(y(haut)) + 8}
                          width={lb + 4}
                          height={3}
                        />
                      )}
                      {i === courante && t > 0 && (
                        <text
                          className={`${P}__valeur`}
                          x={xb + lb / 2}
                          y={Math.round(y(t)) - 6}
                          textAnchor="middle"
                        >
                          {nombre(t)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
              <g aria-hidden="true">
                {colonnes.map((_, i) => {
                  const texte = etiquette(i);
                  if (texte === null) return null;
                  const xb = xBarre(i);
                  const auj = i === courante;
                  const debut = auj && courante === 0;
                  // Une étiquette qui chevaucherait la précédente (écran étroit) n'est pas écrite.
                  const w = largeurTexte(texte);
                  const gauche = auj ? (debut ? xb : xb + lb - w) : xb + lb / 2 - w / 2;
                  if (gauche < finEtiquette + 6) return null;
                  finEtiquette = gauche + w;
                  return (
                    <text
                      key={i}
                      className={`${P}__graduation${auj ? ` ${P}__graduation--aujourdhui` : ''}`}
                      x={auj ? (debut ? xb : xb + lb) : xb + lb / 2}
                      y={HAUT + HT + 18}
                      textAnchor={auj ? (debut ? 'start' : 'end') : 'middle'}
                    >
                      {texte}
                    </text>
                  );
                })}
              </g>
            </svg>
            <Infobulle
              P={P}
              bulle={bulle}
              actif={actif}
              colonne={colonneActive}
              titre={actif >= 0 ? titreInfobulle(actif) : ''}
              avecTemps={avecTemps}
              centre={G + (actif + 0.5) * pasX}
              ecart={pasX / 2 + 4}
              largeur={L}
            />
          </>
        );
      }}
    </CadreDeGraphique>
  );
}

function Infobulle({
  P,
  bulle,
  actif,
  colonne,
  titre,
  avecTemps,
  centre,
  ecart,
  largeur,
}: {
  P: GraphiqueEnBarresProps['classe'];
  bulle: React.RefObject<HTMLDivElement | null>;
  actif: number;
  colonne: ColonneEnBarres | undefined;
  titre: string;
  avecTemps: boolean;
  centre: number;
  ecart: number;
  largeur: number;
}) {
  useInfobulle(bulle, actif, centre, ecart, largeur);
  const t = colonne ? totalColonne(colonne) : 0;
  return (
    <div className={`${P}__infobulle`} aria-hidden="true" hidden={!colonne} ref={bulle}>
      {colonne && (
        <>
          <p className={`${P}__infobulle-titre`}>{titre}</p>
          {avecTemps && t === 0 ? (
            <p className={`${P}__aucune`}>Aucune révision</p>
          ) : (
            <div className={`${P}__lignes`}>
              {[...ETATS].reverse().map((e) => (
                <Fragment key={e.cle}>
                  <span className={`${P}__trait ${P}__trait--${e.cle}`} />
                  <span className={`${P}__nombre`}>{nombre(colonne[e.champ])}</span>
                  <span className={`${P}__nom`}>{e.nom}</span>
                </Fragment>
              ))}
              <div className={`${P}__total`}>
                <span className={`${P}__nombre`}>{nombre(t)}</span>
                <span className={`${P}__nom`}>
                  {avecTemps ? (t > 1 ? 'cartes révisées' : 'carte révisée') : 'au total'}
                </span>
              </div>
              {avecTemps && (
                <div className={`${P}__temps`}>
                  <span className={`${P}__nombre`}>
                    {dureeCourte((colonne.secondes ?? 0) / 60)}
                  </span>{' '}
                  de révision
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
